import { createClient } from '@supabase/supabase-js';
import type { Database } from '@assessor/db/generated';
import { env } from '@/config/env';

/**
 * Cliente Supabase do browser.
 *
 * Usa a chave PUBLICÁVEL — pública por design e protegida por RLS. A chave
 * secreta nunca chega aqui: qualquer operação que precise dela roda em
 * Edge Function ou no worker.
 */
export const supabase = createClient<Database>(env.supabaseUrl, env.supabasePublishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

/** Erro de domínio, já traduzido para o usuário. */
export class AppError extends Error {
  readonly code: string;
  // `cause` já existe em Error a partir do ES2022: sobrescrevemos de propósito
  // para carregar o erro original sem perder a mensagem traduzida.
  override readonly cause?: unknown;

  constructor(message: string, code = 'unknown', cause?: unknown) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.cause = cause;
  }
}

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'E-mail ou senha incorretos.',
  'Invalid login credentials': 'E-mail ou senha incorretos.',
  email_not_confirmed: 'Confirme seu e-mail antes de entrar.',
  user_already_exists: 'Já existe uma conta com este e-mail.',
  'User already registered': 'Já existe uma conta com este e-mail.',
  weak_password: 'A senha precisa ter ao menos 8 caracteres.',
  over_email_send_rate_limit: 'Muitas tentativas. Aguarde alguns minutos.',
  not_authenticated: 'Sua sessão expirou. Entre novamente.',
  not_a_member: 'Você não tem acesso a este workspace.',
  not_platform_admin: 'Acesso restrito à administração da plataforma.',
  invalid_phone: 'Telefone inválido. Use DDD + número.',
  invalid_name: 'Informe seu nome completo.',
  phone_already_linked: 'Este telefone já está vinculado a outra conta.',
  workspace_name_required: 'Dê um nome ao seu workspace.',
};

/**
 * Códigos que ESTE projeto levanta de propósito, com texto para o usuário.
 *
 * Toda `raise exception` das migrações usa um destes, e a mensagem é
 * escrita em português para ser lida — "escolha ao menos um tipo de dado
 * para apagar", "confirmação não confere: digite o nome do workspace".
 *
 * Sem esta lista, essas frases eram trocadas pelo texto genérico da
 * chamada e o usuário via "não foi possível" sem saber o que corrigir. E
 * quem fosse investigar também não via: o motivo não chegava a lugar
 * nenhum.
 *
 * O que fica de fora continua fora. Erro de sintaxe, violação de chave
 * estrangeira, falha de conexão — nada disso é escrito para o cliente, e
 * vazá-lo seria expor a estrutura do banco a quem não deve vê-la.
 */
const CODIGOS_COM_MENSAGEM_NOSSA = new Set([
  'P0001', // raise exception sem código próprio
  'P0002', // não encontrado
  '22023', // parâmetro inválido — o mais usado nas validações
  '42501', // permissão negada
]);

/**
 * Traduz erro de infraestrutura em mensagem para o usuário.
 *
 * Nunca devolve stack trace nem detalhe interno do Postgres ao cliente —
 * o detalhe fica no console (dev) e nos logs do servidor.
 */
export function toAppError(
  error: unknown,
  fallback = 'Algo deu errado. Tente novamente.',
): AppError {
  if (error instanceof AppError) return error;

  const raw = error as { message?: string; code?: string; error_description?: string } | null;
  const rawMessage = raw?.message ?? raw?.error_description ?? '';

  for (const [key, friendly] of Object.entries(MESSAGES)) {
    if (rawMessage.includes(key)) {
      return new AppError(friendly, key, error);
    }
  }

  if (env.isDev && rawMessage) {
    console.error('[AppError]', error);
  }

  // A mensagem do banco, quando o banco a escreveu para ser lida.
  if (raw?.code && CODIGOS_COM_MENSAGEM_NOSSA.has(raw.code) && rawMessage) {
    return new AppError(rawMessage, raw.code, error);
  }

  /*
   * O CÓDIGO VAI JUNTO, sempre.
   *
   * Eu tentei adivinhar quais SQLSTATEs teriam mensagem escrita para o
   * usuário e liberei quatro. Errei: a falha que eu estava investigando
   * não era nenhum deles, e a tela continuou dizendo só "não foi
   * possível" — beco sem saída para quem usa e para quem conserta.
   *
   * O código sozinho não expõe nada. São cinco caracteres, sem nome de
   * tabela, coluna ou consulta. E bastam para saber o que aconteceu sem
   * mais uma rodada de tentativa e erro.
   *
   * A regra que fica: mensagem de erro que não permite agir é defeito,
   * não proteção.
   */
  const comCodigo = raw?.code ? `${fallback} (código ${raw.code})` : fallback;
  return new AppError(comCodigo, raw?.code ?? 'unknown', error);
}
